import React, { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { useRoles, useUpdateMemberRole } from "../hooks/useMembers"

export function MemberDrawer({ open, member, onClose, onSuccess }) {
  const { toast } = useToast()
  const { roles, fetch: fetchRoles } = useRoles()
  const updateHook = useUpdateMemberRole()
  const [roleSlug, setRoleSlug] = useState("")

  useEffect(() => { if (open) fetchRoles() }, [open, fetchRoles])
  useEffect(() => { if (member) setRoleSlug(member.role.slug) }, [member])

  async function handleSave() {
    try {
      await updateHook.mutate(member.id, roleSlug)
      toast({ title: "Member updated", description: member.user.name })
      onSuccess?.()
      onClose()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : (errors?.message || "Something went wrong")
      toast({ variant: "destructive", title: "Error", description: msg })
    }
  }

  if (!member) return null

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>Edit Member</SheetTitle>
          <SheetDescription>Update this member's role in the organization.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={member.user.name} readOnly className="bg-slate-50 text-slate-600" />
          </div>

          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={member.user.email} readOnly className="bg-slate-50 text-slate-600" />
          </div>

          <div className="space-y-1.5">
            <Label>
              Role <span className="text-red-500">*</span>
            </Label>
            <Select value={roleSlug} onValueChange={setRoleSlug}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r.slug} value={r.slug}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <Button variant="ghost" onClick={onClose} disabled={updateHook.isPending}>Cancel</Button>
          <Button onClick={handleSave} disabled={updateHook.isPending || !roleSlug} className="gap-2">
            {updateHook.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
